import {
  DEBUG_ACCOUNT, REPLAY_TIMEFRAMES, strategyFor, baselineFor,
} from '../components/DynaManLiveSimulator';
import { normaliseManagement } from './replaySession';

/**
 * THE REQUEST A SESSION SENDS — built once, used by the setup modal and by a mandate change.
 *
 * <h2>Why a builder and not an object literal at each call site</h2>
 *
 * There are now three ways to start a replay: the research dialog, the setup modal, and switching
 * the mandate on the transport. This repository has already shipped the failure that follows from
 * letting each one assemble its own payload — three run paths from ONE dialog sent three different
 * strategies, and only one of them matched any published figure.
 *
 * <p>So the levers are derived rather than restated: `strategyFor` supplies the provider's locked
 * physics and `baselineFor` its measured baseline, both from `DynaManLiveSimulator`, which is where
 * the research dialog reads them too. A mandate change then cannot run different rules from the
 * launch that preceded it.
 *
 * <h2>What it deliberately does NOT carry</h2>
 *
 * None of the dialog's optional research levers — no CTS direction rule, no TrendPulse parameters,
 * no exit sandbox mechanics beyond the provider's own baseline, no target overrides. A session is
 * the measured configuration of one engine, so the request is the baseline and nothing else. An
 * operator who wants to sweep a lever has the research dialog, which is where sweeping belongs.
 *
 * <p>THE ONE EXCEPTION is the trade-management block, and it is an exception on purpose: those
 * four mechanics change how a premise is MANAGED rather than which signals are taken, so they are
 * what an operator watching a session bar by bar is in a position to form a view about. They
 * default to the control, so a request that carries them unchanged is the baseline request.
 */

/**
 * The bars a replay walks: the signal timeframe alone.
 *
 * <p>Re-exported from the live simulator rather than restated. One frame per chart candle is the
 * only arrangement in which Step moves both the chart and the HUD, and a second copy of that list
 * is how a run comes to replay 169,144 bars when it meant 8,466.
 */
export const SESSION_TIMEFRAMES = REPLAY_TIMEFRAMES;

/** The account every session is priced with — the same object the human book is charged against. */
export const SESSION_ACCOUNT = DEBUG_ACCOUNT;

/**
 * The four management levers as the API names them.
 *
 * <h2>The booleans are sent even when FALSE; the bar count is OMITTED when 0</h2>
 *
 * Not an inconsistency — they are different kinds of value. A boolean lever has a switch on screen,
 * and expressing its "off" by absence is exactly how `bandMacroGate` came to be a control the wire
 * never carried: the backend builds its explicit-lever set from nullability, so a dropped `false`
 * reads as "not set" and hands the lever back to the provider profile. Sending it is one field and
 * removes a whole class of failure.
 *
 * <p>`maxBarsInTrade` has no switch. Zero IS the engine's off state and also its default, so
 * omitting it and sending 0 are identical in effect — and omitting keeps an untouched section to
 * three fields rather than four, which is what makes a captured payload readable.
 */
export function managementPayload(management) {
  const m = normaliseManagement(management);
  const payload = {
    fanExit: m.fanExit,
    fanReload: m.fanReload,
    atrTrailingStop: m.atrTrailingStop,
  };
  if (m.maxBarsInTrade > 0) payload.maxBarsInTrade = m.maxBarsInTrade;
  // THE TUNING PARAMETERS, omitted when the operator left them alone. Unlike the three booleans
  // above, an absent one is not a decision a reader made -- it is the engine's own default, and
  // sending it explicitly would pin a value that may move under them in a later release.
  if (m.fanFractalWidth > 0) payload.fanFractalWidth = m.fanFractalWidth;
  if (m.reloadEma > 0) payload.reloadEma = m.reloadEma;
  if (m.structuralTolerance > 0) payload.structuralTolerance = m.structuralTolerance;
  // SENT FALSE EXPLICITLY, like the three mechanic switches and unlike the numbers: the backend
  // builds its explicit-lever set from nullability, so a dropped `false` reads as "not set" and
  // hands the lever back to the provider profile.
  payload.sameBarEntry = m.sameBarEntry;
  payload.crossoverRebound = m.crossoverRebound;
  // ALWAYS SENT, including the default 1 -- see the note on the field. One is a real setting, not
  // the absence of one.
  payload.maxTranches = m.maxTranches;
  // THE SWITCH TRAVELS EVEN WHEN FALSE, like every other boolean lever here: the backend builds
  // its explicit-lever set from NULLABILITY, so a dropped `false` reads as "not set" and hands
  // the lever back to the provider profile. The LENGTH is omitted when unset, because 0 is not a
  // setting the engine accepts.
  // ALWAYS SENT, including LEGACY. Like `maxTranches`, this has no "unset" reading -- a run uses
  // one engine or the other -- so omitting it would make a captured request ambiguous between a
  // legacy run and a client that had never heard of the choice.
  payload.engineMode = m.engineMode;
  // ALWAYS SENT TOO, and for the stronger version of the same reason: the backend DEFAULTS this
  // one to true, so an omitted `false` would not merely be ambiguous -- it would run the preset on
  // a request that asked for it to be off. The only state the wire can express for "no preset" is
  // an explicit false.
  payload.enginePreset = m.enginePreset;
  if (m.directBreakevenR > 0) payload.directBreakevenR = m.directBreakevenR;
  payload.chargedContinuation = m.chargedContinuation;
  if (m.continuationTrendSma > 0) payload.continuationTrendSma = m.continuationTrendSma;
  return payload;
}

/**
 * @param provider     the mandate: 'PTA' | 'PTB' | 'CTS'
 * @param symbol       the chart's symbol
 * @param customSeries an uploaded series, or null. Present switches the helper to POST, so
 *                     omitting it would silently run the STORED symbol of the same name
 * @param management   the session's trade-management block. Omitted gives the measured control,
 *                     which is what every figure in CLAUDE.md was taken with
 */
export function buildSessionRequest({
  provider, symbol, customSeries = null, management = null,
}) {
  const baseline = baselineFor(provider) || {};
  return {
    ...strategyFor(provider),
    signalProvider: provider,
    // ONE PROVIDER, and the structural breakout is not attached beside it. A run with two entry
    // sources is a measurement of neither, and every published per-provider figure was taken with
    // this on.
    disableStructuralEntry: true,
    // The provider's own measured baseline. PTA and PTB want the macro gate; CTS wants it off and
    // its time ejector at 5 bars. Carrying the wrong one is not an error, it is a different and
    // worse result reported under the right name.
    macroGate: !!baseline.macroGate,
    ...(baseline.ctsExit || {}),
    // AFTER the baseline, so an operator's own choice wins over the provider's defaults. None of
    // the three baselines sets any of these four today, so the order is a statement rather than a
    // fix -- and it is the order that would be wanted if one ever did.
    ...managementPayload(management),
    ...SESSION_ACCOUNT,
    timeframes: SESSION_TIMEFRAMES,
    // EVERY BAR. A windowed replay is chosen by the BACKEND — it hunts the densest cluster of
    // decisions — so asking for one would hand the session's window to a heuristic. The session's
    // own window is applied on this side, against frames that all exist.
    maxBars: 0,
    symbol,
    customSeries,
  };
}

/**
 * The same request, asking for ONE frame.
 *
 * <h2>This is how a session draws structure it is not trading</h2>
 *
 * `SimulationService` walks the ATTACHED providers to build `timeBands`, so a CTS-mandate run
 * carries no PTA bands at all — and the levels cannot be computed in the browser, because they are
 * price extremes of the engine's own bars.
 *
 * <p>But `timeBands` is written to the sink BEFORE the replay window is chosen, so it is complete
 * whatever `maxBars` says. One frame is therefore a few hundred bytes carrying the whole band
 * table, against about half a megabyte for a full timeline — which is what makes "draw PTA while
 * trading CTS" affordable rather than three full replays.
 *
 * <p>`maxBars: 1` and not `2`: the frames are discarded, and asking for the smallest thing the
 * endpoint will answer with is the clearest statement that only the bands are wanted.
 */
export function buildBandOnlyRequest({
  provider, symbol, customSeries = null, management = null,
}) {
  // The management levers ride along even though the frames are discarded. They cannot change the
  // band table -- it is written before the replay window is chosen and the levers only touch how a
  // premise is managed -- so this is about the two requests being ONE request with one field
  // differing, rather than two assemblies that could drift.
  return {
    ...buildSessionRequest({ provider, symbol, customSeries, management }),
    maxBars: 1,
  };
}
